import React from 'react';

import { SortableTable } from '~/components/core';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import EmptyState from '~/components/core/EmptyState';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import type { PaginationProps } from '~/components/core/Tables/SortableTable/TablePaginationSection/types';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import Switch from '~/components/ui/atoms/Switch';
import type { PaginationState } from '~/hooks/useIdBasedPagination';
import { subOrgIdToNameDict } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

// Extended interface for frontend-specific properties
interface WorkflowRuleModelWithSummary {
  id: string;
  display_name: string;
  is_enabled: boolean;
  is_all_lobs: boolean;
  lobs: string[];
  event_key: string;
  workflow_name: string;
  context: string;
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
  [key: string]: unknown;
}

interface Organization {
  id: number;
  sub_organizations_enabled: boolean;
  sub_organizations?: { id: number; name: string }[];
  [key: string]: unknown;
}

interface ColumnDefinition {
  id: string;
  label: string;
  specialCell?: (rule: WorkflowRuleModelWithSummary) => React.ReactNode | string;
  numeric?: boolean;
  disableSort?: boolean;
  width?: string;
}

interface WorkflowRulesTableProps {
  organization: Organization;
  rules: WorkflowRuleModelWithSummary[];
  isLoading: boolean;
  isError: boolean;
  isProcessing: boolean;
  pagination: PaginationState;
  sortByColumn: { id: string; order: 'asc' | 'desc' };
  onSortByColumn: (event: React.MouseEvent, sortInfo: { id: string; order: 'asc' | 'desc' }) => void;
  onRefresh: () => Promise<void>;
  onEditRule: (rule: WorkflowRuleModelWithSummary) => void;
  onDeleteRule: (rule: WorkflowRuleModelWithSummary) => Promise<void>;
  onEnabledChanged: (rule: WorkflowRuleModelWithSummary) => Promise<void>;
}

export const WorkflowRulesTable: React.FC<WorkflowRulesTableProps> = ({
  organization,
  rules,
  isLoading,
  isError,
  isProcessing,
  pagination,
  sortByColumn,
  onSortByColumn,
  onRefresh,
  onEditRule,
  onDeleteRule,
  onEnabledChanged,
}) => {
  const { lobConfigurationsDict } = useLobConfiguration();

  // Create pagination props following established pattern
  const paginationProps: PaginationProps = {
    page: pagination.page,
    count: pagination.totalCount,
    rowsPerPage: pagination.rowsPerPage,
    onChangePage: (_event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
      pagination.setPage(page);
    },
    onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => {
      pagination.setRowsPerPage(parseInt(event.target.value, 10));
    },
    rowsPerPageOptions: [10, 25, 50, 100],
  };

  const actionOptions = (rule: WorkflowRuleModelWithSummary) => [
    {
      onClick: () => onEditRule(rule),
      node: 'Edit',
      key: 'editRule',
    },
    {
      onClick: () => onDeleteRule(rule),
      withConfirmProps: {
        title: 'Delete Rule?',
        contentText: `Are you sure you want to delete "${rule.display_name}"?`,
        primaryButtonName: 'Delete',
      },
      node: 'Delete',
      key: 'deleteRule',
    },
  ];

  const enabledCell = (rule: WorkflowRuleModelWithSummary) => (
    <Switch onChange={() => onEnabledChanged(rule)} disabled={isProcessing} checked={rule.is_enabled} />
  );

  const actionsCell = (rule: WorkflowRuleModelWithSummary) => (
    <div>
      <ThreeDotsMenu options={actionOptions(rule)} />
    </div>
  );

  const columns: ColumnDefinition[] = [
    { id: 'display_name', label: 'Rule Name' },
    {
      id: 'lob',
      label: 'LOB',
      specialCell: (rule: WorkflowRuleModelWithSummary) =>
        rule.is_all_lobs
          ? 'All'
          : rule.lobs.map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict)).join(', '),
    },
    { id: 'event_key', label: 'Event' },
    { id: 'workflow_name', label: 'Workflow Name' },
    {
      id: 'enable',
      numeric: false,
      label: 'Enable',
      specialCell: enabledCell,
      disableSort: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '90px',
      specialCell: actionsCell,
      disableSort: true,
    },
  ];

  if (organization.sub_organizations_enabled) {
    columns.splice(1, 0, {
      id: 'sub_organization_ids',
      label: 'Sub Organizations',
      specialCell: (rule: WorkflowRuleModelWithSummary) => {
        const subOrgIdToName = subOrgIdToNameDict(organization);
        return rule.is_all_sub_orgs ? 'All' : rule.sub_organization_ids.map((id) => subOrgIdToName[id]).join(', ');
      },
      disableSort: true,
    });
  }

  if (isLoading || isError) {
    return <SkeletonTable rowsCount={5} columnsCount={7} isError={isError} />;
  }

  // Counter with refresh following established pattern
  const counterViewText = (
    <CounterWithRefresh
      onRefresh={onRefresh}
      isLoading={isLoading}
      pagination={pagination}
      refreshTooltipText="Refresh workflow rules"
      refreshAriaLabel="Refresh workflow rules"
    />
  );

  return (
    <>
      <SortableTable
        columns={columns}
        rows={rules}
        stickyHeader
        defaultOrderColumn={columns.findIndex((column) => column.id === sortByColumn.id)}
        order={sortByColumn.order}
        paginationProps={paginationProps}
        onSortByColumn={onSortByColumn}
        emptyStateComponent={<EmptyState subtitle="No workflow rules found" />}
        counterViewText={counterViewText}
      />
      {pagination.resultsTruncated && (
        <div className="warning text-orange-600 p-2">
          Results limited to {pagination.totalCount}. Refine filters for complete results.
        </div>
      )}
    </>
  );
};
